import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Loader2, ArrowLeft, FileText } from 'lucide-react';

import { API } from '../config';
import { processMarkdownContent } from '../utils/markdownHelper';

const Page = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/pages/${slug}`)
      .then(r => setPage(r.data))
      .catch(e => setErr(e.response?.data?.detail || 'Page not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (page?.title) document.title = `${page.title} – TrainPanda`;
  }, [page]);

  if (loading) return <div className="py-32 text-center text-gray-400"><Loader2 className="animate-spin mx-auto" /></div>;
  if (err) {
    return (
      <div className="py-20 text-center">
        <FileText size={32} className="mx-auto text-gray-300 mb-3" />
        <div className="text-gray-500 mb-4">{err}</div>
        <Link to="/" className="text-tp-violet font-semibold inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to home</Link>
      </div>
    );
  }
  if (!page) return null;

  return (
    <article className="max-w-3xl mx-auto px-4 py-8 md:py-12">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-tp-violet mb-5"><ArrowLeft size={14} /> Home</Link>
      <h1 data-testid="page-title" className="text-3xl md:text-4xl font-extrabold text-tp-ink leading-tight">{page.title}</h1>
      {page.updated_at && (
        <div className="text-xs text-tp-ink-muted mt-2">
          Last updated: {new Date(page.updated_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      )}

      <div className="prose prose-slate max-w-none mt-6">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            h2: (props) => <h2 className="text-2xl font-bold text-tp-ink mt-8 mb-3" {...props} />,
            h3: (props) => <h3 className="text-xl font-bold text-tp-ink mt-6 mb-2" {...props} />,
            p:  (props) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
            ul: (props) => <ul className="list-disc pl-6 space-y-1.5 mb-4 text-gray-700" {...props} />,
            ol: (props) => <ol className="list-decimal pl-6 space-y-1.5 mb-4 text-gray-700" {...props} />,
            a:  (props) => <a className="text-tp-violet underline hover:no-underline" {...props} />,
            strong: (props) => <strong className="text-tp-ink" {...props} />,
            blockquote: (props) => <blockquote className="border-l-4 border-tp-violet bg-tp-violet-soft pl-4 py-2 my-4 italic text-gray-700" {...props} />,
          }}
        >
          {processMarkdownContent(page.content_markdown || '*This page is empty.*')}
        </ReactMarkdown>
      </div>
    </article>
  );
};

export default Page;
